import { clsx } from 'clsx';
import { cn } from '../../utilities/cn';
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as Tooltip from '@radix-ui/react-tooltip';

// Variants on cva, the shadcn way; merged with `cn` below so a compound class replaces the base class it conflicts with.
const base =
  'flex items-center justify-center transition-colors duration-300 cursor-pointer min-w-min justify-center items-center shrink-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-interactive';

export const buttonIconStyles = cva(base, {
  variants: {
    variant: {
      primary: 'text-content-lighter bg-surface-interactive border border-transparent',
      secondary: 'text-content-dark border border-stroke-light bg-surface-white',
      inverse: 'text-surface-white bg-transparent border border-transparent focus-visible:ring-surface-white',
      tertiary: 'text-content-interactive bg-transparent border border-transparent',
      destructive: 'text-danger-dark bg-transparent border border-transparent',
    },
    size: {
      sm: '!p-xs rounded-radius-sm text-sm',
      lg: '!p-sm rounded-radius-md text-lg',
    },
    selected: {
      true: '',
      false: '',
    },
    disabled: {
      true: 'opacity-50 hover:cursor-not-allowed',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },

  compoundVariants: [
    // Hover State Variants
    {
      variant: 'primary',
      disabled: false,
      className: 'hover:bg-surface-interactive-dark hover:text-content-lighter',
    },
    {
      variant: 'secondary',
      disabled: false,
      className: 'hover:bg-surface-interactive-lighter hover:border-stroke-interactive hover:text-content-interactive',
    },
    {
      variant: 'inverse',
      disabled: false,
      className: 'hover:bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      disabled: false,
      className: 'hover:text-content-interactive hover:bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      disabled: false,
      className: 'hover:bg-danger-light',
    },

    // Selected State Variants
    {
      variant: 'primary',
      selected: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive !text-content-interactive',
    },
    {
      variant: 'inverse',
      selected: true,
      className: 'bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      selected: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      className: 'bg-danger-light',
    },

    //selected and disabled state variants
    {
      variant: 'primary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'tertiary',
      selected: true,
      disabled: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      disabled: true,
      className: 'bg-danger-light',
    },
  ],
});

export interface ButtonIconProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonIconStyles>, 'disabled'> {
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
  /**
   * Required: icon-only buttons have no visible text, so this is their only accessible name
   */
  'aria-label': string;
  /**
   * When set, the icon button is rendered as disabled and wrapped in a Tooltip
   * with this string as content. Internally renders as a <div> to allow Tooltip
   * hover events.
   */
  disabledReason?: string;
}

export const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(
  (
    { className, variant, size, children, selected = false, disabled = false, disabledReason, onClick, ...rest },
    ref,
  ) => {
    const isDisabled = disabled || !!disabledReason;
    const variants = React.useMemo(
      () => cn(buttonIconStyles({ variant, size, selected, disabled: isDisabled })),
      [variant, size, selected, isDisabled],
    );

    if (disabledReason) {
      return (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div
                role="button"
                tabIndex={0}
                aria-disabled
                aria-label={rest['aria-label']}
                className={clsx(variants, className)}>
                {children}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-sm py-xs rounded-radius-sm bg-surface-darker text-white text-body-small shadow-raised z-50"
                sideOffset={4}>
                {disabledReason}
                <Tooltip.Arrow className="fill-surface-darker" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return (
      <button
        ref={ref}
        className={clsx(variants, className)}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : onClick}
        {...rest}>
        {children}
      </button>
    );
  },
);

ButtonIcon.displayName = 'ButtonIcon';
